import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchEmployeeBenefits, createEmployeeBenefit, updateEmployeeBenefit, fetchBenefitTypes } from '../../../../../services/salary';
import { fetchEmployees } from '../../../../../services/employee';
import CustomPagination from '../../../../../components/common/CustomPagination.js';

const EmployeeBenefits = () => {
  const { t } = useTranslation(['common', 'employees', 'salary']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    employee: '',
    benefit_type: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    employee: '',
    benefit_type: '',
    amount: '',
    amount_type: 'fixed',
    percentage_of: '',
    effective_date: '',
    end_date: '',
    provider: '',
    policy_number: '',
    notes: '',
    is_active: true
  });
  const [employees, setEmployees] = useState([]);
  const [benefitTypes, setBenefitTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchEmployeeBenefits(params);
      
      if (response.results) {
        setBenefits(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setBenefits(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch employee benefits');
      setBenefits([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (modalOpen) {
        try {
          const [employeesRes, typesRes] = await Promise.all([
            fetchEmployees({ page_size: 1000 }).catch(() => ({ results: [] })),
            fetchBenefitTypes({ page_size: 1000, is_active: 'true' }).catch(() => ({ results: [] }))
          ]);
          
          setEmployees(employeesRes.results || employeesRes || []);
          setBenefitTypes(typesRes.results || typesRes || []);
        } catch (err) {
          console.error('Failed to fetch dropdown data:', err);
        }
      }
    };
    
    fetchDropdownData();
  }, [modalOpen]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (benefit = null) => {
    if (benefit) {
      setEditId(benefit.id);
      setForm({
        employee: benefit.employee?.id || benefit.employee || '',
        benefit_type: benefit.benefit_type?.id || benefit.benefit_type || '',
        amount: benefit.amount || '',
        amount_type: benefit.amount_type || 'fixed',
        percentage_of: benefit.percentage_of || '',
        effective_date: benefit.effective_date || '',
        end_date: benefit.end_date || '',
        provider: benefit.provider || '',
        policy_number: benefit.policy_number || '',
        notes: benefit.notes || '',
        is_active: benefit.is_active !== undefined ? benefit.is_active : true
      });
    } else {
      setEditId(null);
      setForm({
        employee: '',
        benefit_type: '',
        amount: '',
        amount_type: 'fixed',
        percentage_of: '',
        effective_date: '',
        end_date: '',
        provider: '',
        policy_number: '',
        notes: '',
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      employee: '',
      benefit_type: '',
      amount: '',
      amount_type: 'fixed',
      percentage_of: '',
      effective_date: '',
      end_date: '',
      provider: '',
      policy_number: '',
      notes: '',
      is_active: true
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.employee) {
      setSnackbar({ open: true, message: 'Employee is required', severity: 'error' });
      return;
    }

    if (!form.benefit_type) {
      setSnackbar({ open: true, message: 'Benefit type is required', severity: 'error' });
      return;
    }

    if (!form.amount || parseFloat(form.amount) <= 0) {
      setSnackbar({ open: true, message: 'Amount is required and must be greater than 0', severity: 'error' });
      return;
    }

    if (form.amount_type === 'percentage' && !form.percentage_of) {
      setSnackbar({ open: true, message: 'Percentage of is required when amount type is percentage', severity: 'error' });
      return;
    }

    if (!form.effective_date) {
      setSnackbar({ open: true, message: 'Effective date is required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        employee: form.employee,
        benefit_type: form.benefit_type,
        amount: form.amount,
        amount_type: form.amount_type,
        percentage_of: form.amount_type === 'percentage' ? form.percentage_of : null,
        effective_date: form.effective_date,
        end_date: form.end_date || null,
        provider: form.provider || null,
        policy_number: form.policy_number || null,
        notes: form.notes || null,
        is_active: form.is_active
      };

      if (editId) {
        await updateEmployeeBenefit(editId, payload);
        setSnackbar({ open: true, message: 'Employee benefit updated successfully', severity: 'success' });
      } else {
        await createEmployeeBenefit(payload);
        setSnackbar({ open: true, message: 'Employee benefit created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          Object.values(err.response?.data || {}).flat().join(', ') ||
                          err.message || 
                          'Failed to save employee benefit';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return benefits.map((item, index) => ({
      ...item,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [benefits, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('salary:benefits.employee.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_name',
      headerName: t('salary:benefits.employee.columns.employee'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'employee_id',
      headerName: t('salary:benefits.employee.columns.employeeId'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'benefit_type_name',
      headerName: t('salary:benefits.employee.columns.benefitType'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'amount',
      headerName: t('salary:benefits.employee.columns.amount'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const amount = parseFloat(params.value || 0);
        const amountType = params.row.amount_type || 'fixed';
        if (amountType === 'percentage') {
          return `${amount}%`;
        }
        return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    },
    {
      field: 'amount_type',
      headerName: t('salary:benefits.employee.columns.amountType'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const type = params.value || 'fixed';
        return type === 'fixed' ? t('salary:common.fixed') : t('salary:common.percentage');
      }
    },
    {
      field: 'effective_date',
      headerName: t('salary:benefits.employee.columns.effectiveDate'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'is_active',
      headerName: t('salary:benefits.employee.columns.status'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('salary:common.active') : t('salary:common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ], [t]);

  if (loading && benefits.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('salary:benefits.employee.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('salary:benefits.employee.addBenefit')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('employees:search')}
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by employee..."
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Employee"
                fullWidth
                value={filters.employee}
                onChange={(e) => handleFilterChange('employee', e.target.value)}
                placeholder="Employee ID"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('employees:status')}
                fullWidth
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">{t('salary:common.all')}</option>
                <option value="true">{t('salary:common.active')}</option>
                <option value="false">{t('salary:common.inactive')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Employee Benefit Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? t('common:buttons.edit') + ' ' + t('salary:benefits.employee.title') : t('salary:benefits.employee.addBenefit')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.employee_id || ''})`}
                  value={employees.find(e => e.id === form.employee) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('employee', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee"
                      required
                      placeholder="Select employee"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={benefitTypes}
                  getOptionLabel={(option) => `${option.name || ''} (${option.code || ''})`}
                  value={benefitTypes.find(bt => bt.id === form.benefit_type) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('benefit_type', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Benefit Type"
                      required
                      placeholder="Select benefit type"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('salary:benefits.employee.columns.amountType')}
                  value={form.amount_type}
                  onChange={(e) => handleFormChange('amount_type', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="fixed">{t('salary:common.fixed')}</option>
                  <option value="percentage">{t('salary:common.percentage')}</option>
                </TextField>
              </Grid>
              {form.amount_type === 'percentage' && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Percentage Of"
                    value={form.percentage_of}
                    onChange={(e) => handleFormChange('percentage_of', e.target.value)}
                    SelectProps={{ native: true }}
                    required
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="">Select...</option>
                    <option value="base_salary">Base Salary</option>
                    <option value="gross_salary">Gross Salary</option>
                  </TextField>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:benefits.employee.columns.amount')}
                  type="number"
                  value={form.amount}
                  onChange={(e) => handleFormChange('amount', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText={form.amount_type === 'percentage' ? 'Enter percentage (e.g., 5 for 5%)' : 'Enter amount'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:benefits.employee.columns.effectiveDate')}
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => handleFormChange('effective_date', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => handleFormChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Provider"
                  value={form.provider}
                  onChange={(e) => handleFormChange('provider', e.target.value)}
                  placeholder="e.g., HMO Company Name"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Policy Number"
                  value={form.policy_number}
                  onChange={(e) => handleFormChange('policy_number', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={form.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:status')}
                  value={form.is_active ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_active', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="true">{t('salary:common.active')}</option>
                  <option value="false">{t('salary:common.inactive')}</option>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeBenefits;

